import { MouseEvent, useState } from 'react';
import cn from 'classnames';
import { useAtom } from 'jotai';
import { Text, useToast } from '@pairy/pds-react';

import useUpdateViewerPageState from '../../../../../../hooks/useUpdateViewerPageState';
import useGetFileDownloadLink from '../../../../../../hooks/useGetFileDownloadLink';
import { GOOGLE_EVENT_ID } from '../../../../../../constant/googleEventId';
import useFileDownload from '../../../../../../hooks/mutation/file/useFileDownload';
import { isDisableHelpersAtom } from '../../../../../../store/index.atom';
import useDownloadPdfFile from '../../../../../../hooks/useDownloadPdfFile';
import * as Styled from '../index.styles';

interface DesktopPdfDownloadButtonProps {
  translateKoToEn: (item: string) => string;
  fileName: string;
  isBulk: boolean;
  isDownloadEnabled: boolean;
  ctaFormPage?: number;
  isRedirectCtaFormAppropriate?: boolean;
}

const DesktopPdfDownloadButton = ({
  fileName,
  translateKoToEn,
  isBulk = false,
  isDownloadEnabled = false,
  ctaFormPage,
  isRedirectCtaFormAppropriate = false,
}: DesktopPdfDownloadButtonProps) => {
  const toast = useToast();

  const [isDisableHelpers, setIsDisableHelpers] = useAtom(isDisableHelpersAtom);

  const [isHovering, setIsHovering] = useState(false);
  const { updateCurrentPage } = useUpdateViewerPageState();

  const handleMouseOver = () => {
    setIsHovering(true);
  };

  const handleMouseOut = () => {
    setIsHovering(false);
  };

  const { isLoading, downloadPdfFile } = useDownloadPdfFile();
  const { mutate: mutateFileDownload } = useFileDownload(isBulk);

  const { getDownloadLink } = useGetFileDownloadLink({
    handleGetDownloadSuccess: async (downloadUrl: string) => {
      await downloadPdfFile(downloadUrl, fileName, mutateFileDownload);
    },
  });

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (isDisableHelpers) {
      setIsDisableHelpers(false);
      return;
    }

    /*문의 양식 제출 전에 다운로드 버튼을 클릭했을 경우에, 경고 메세지와 함께 폼이 있는 페이지로 이동시킨다*/
    if (isRedirectCtaFormAppropriate && ctaFormPage) {
      toast(`${translateKoToEn('download-alert')}`);
      updateCurrentPage(ctaFormPage);
      return;
    }

    getDownloadLink();
  };

  if (!isDownloadEnabled) {
    return null;
  }

  return (
    <>
      <Styled.DesktopButton
        id={GOOGLE_EVENT_ID.download}
        className={cn({
          disable: isDisableHelpers,
          isLoading: isLoading,
        })}
        variant="zinc_500"
        radius={9999}
        paddingX={16}
        onClick={handleClick}
        onMouseOver={handleMouseOver}
        onMouseOut={handleMouseOut}
      >
        <Styled.Spinner
          isLoading={isLoading}
          icon={isLoading ? 'ph:circle-notch' : 'ph:download-simple'}
          size={19}
          color={isLoading ? 'zinc_400' : 'black'}
        />
        {(isHovering || isLoading) && (
          <Text
            id={GOOGLE_EVENT_ID.download}
            variant="lg-medium"
            marginLeft={8}
            color={isLoading ? 'zinc_400' : 'black'}
            style={{
              whiteSpace: 'nowrap',
            }}
          >
            {translateKoToEn('download')}
          </Text>
        )}
      </Styled.DesktopButton>
    </>
  );
};

export default DesktopPdfDownloadButton;
